import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import * as Yup from 'yup';

import { localeDetails } from '~/components/CmsMain/localeGlobals';

import { COUNTRY_TO_STATE_MAP } from '../../Types';

const CustomFieldsContext = React.createContext({});

function CustomFieldsContextProvider(props) {
  const { children, customFieldsConfigurations } = props;

  const contextValues = {
    customFieldsConfigurations,
    additionalDataValidations: getAdditionalDataValidations(customFieldsConfigurations),
  };

  return <CustomFieldsContext.Provider value={contextValues}>{children}</CustomFieldsContext.Provider>;
}

const createStringValidation = (field) =>
  field.mandatory && field.active !== false
    ? Yup.string()
        .transform((value) => value?.trim())
        .test('notEmpty', 'Required', (value) => !!value)
    : Yup.string().nullable();

// The one place the field types the FNOL/incident-config forms validate are defined. Keyed rather than
// switched so ADDITIONAL_DATA_FIELD_TYPES below is derived from the dispatch itself and cannot drift from
// it; a type with no validation maps to undefined and its key is omitted (see the omitBy at the bottom).
const ADDITIONAL_DATA_VALIDATION_BY_FIELD_TYPE = {
  string: (field) => createStringValidation(field),
  multiline_string: (field) => createStringValidation(field),
  select: (field) =>
    field.mandatory && field.active !== false
      ? Yup.string()
          .oneOf(field.options.map((o) => o.id))
          .required('Required')
      : Yup.string()
          .nullable()
          .oneOf([...field.options.map((o) => o.id), null]), // @see https://github.com/jquense/yup/issues/104
  // `.required()` on top of `.min(1)`: min has no array to measure when the value is absent and passes, so
  // without it a mandatory multiselect is the one type that accepts a field no stored key covers.
  multiselect: (field) =>
    field.mandatory && field.active !== false
      ? Yup.array()
          .of(Yup.string().oneOf(field.options.map((o) => o.id)))
          .min(1, 'Required')
          .required('Required')
      : Yup.array()
          .nullable()
          .of(Yup.string().oneOf([...field.options.map((o) => o.id), null])),
  // Validate against the org region's state list when the region has one. For regions without a state map
  // the renderer hides the field, so any stored value is unreachable from the UI — accept anything
  // (nullable string) so legacy values don't block unrelated saves.
  us_state_select: (field) => {
    const region = localeDetails.locale.region;
    const statesMap = COUNTRY_TO_STATE_MAP[region];
    if (!statesMap) {
      return Yup.string().nullable();
    }
    const stateKeys = Object.keys(statesMap);
    return field.mandatory && field.active !== false
      ? Yup.string().oneOf(stateKeys).required('Required')
      : Yup.string()
          .nullable()
          .oneOf([...stateKeys, null]); // @see https://github.com/jquense/yup/issues/104
  },
  date: (field) =>
    field.mandatory && field.active !== false
      ? field?.disableFuture
        ? Yup.date().nullable().max(new Date()).required('Required')
        : Yup.date().nullable().required('Required')
      : field?.disableFuture
        ? Yup.date().nullable().max(new Date())
        : Yup.date().nullable(),
  year: (field) => {
    const yearValidator = (year) => year >= 1920 && year <= new Date().getFullYear() + 1;

    return field.mandatory && field.active !== false
      ? Yup.mixed().test('is-valid', 'Valid year is required', yearValidator)
      : Yup.mixed()
          .nullable()
          .test(
            'is-valid',
            'Invalid year',
            (year) => year === '' || year === undefined || year === null || yearValidator(year)
          );
  },
  time: (field) => {
    const regex = /^([0-1][0-9]|2[0-3]):[0-5][0-9](|:[0-5][0-9])$/;
    return field.mandatory && field.active !== false
      ? Yup.string().nullable().matches(regex, 'Invalid Time Format').required('Required')
      : Yup.string().nullable().matches(regex, 'Invalid Time Format');
  },
  number: (field) =>
    field.mandatory && field.active !== false ? Yup.number().required('Required') : Yup.number().nullable(),
  monetary: (field) =>
    field.mandatory && field.active !== false ? Yup.number().required('Required') : Yup.number().nullable(),
  boolean: (field) =>
    field.mandatory && field.active !== false ? Yup.bool().required('Required') : Yup.bool().nullable(),
  contact: (field) =>
    field.mandatory && field.active !== false ? Yup.number().required('Required') : Yup.number().nullable(),
  yes_no_na_unknown: (field) => {
    const options = ['yes', 'no'];
    if (!field?.disable_na) options.push('na');
    if (!field?.disable_unknown) options.push('unknown');

    return field.mandatory && field.active !== false
      ? Yup.string().nullable().oneOf(options).required('Required')
      : Yup.string()
          .nullable()
          .oneOf([...options, null]);
  },
  location: (field) =>
    field.mandatory && field.active !== false
      ? Yup.object().test(
          'location-validation',
          'Required',
          (value) => value?.country?.length > 0 && value?.city?.length > 0
        )
      : undefined,
  pre_defined_fields: () => undefined,
  pre_defined_fields_array: () => undefined,
  make_ncic_code: (field) =>
    field.mandatory && field.active !== false ? Yup.string().required('Required') : Yup.string(),
};

/** Every custom field type getAdditionalDataValidations knows how to validate. */
const ADDITIONAL_DATA_FIELD_TYPES = Object.keys(ADDITIONAL_DATA_VALIDATION_BY_FIELD_TYPE);

const getAdditionalDataValidations = (customFieldsConfigurations) => {
  return (
    _.chain(customFieldsConfigurations)
      .mapKeys((field) => field.id)
      .mapValues((field) => {
        // Own-property lookup: a plain object answers `constructor` and `toString` off its prototype, and
        // a field type spelled like one of those must reach the throw below rather than call them.
        if (!_.has(ADDITIONAL_DATA_VALIDATION_BY_FIELD_TYPE, field.type)) {
          throw Error(`Unknown custom field type: ${field.type} - field id: ${field.id}`);
        }
        return ADDITIONAL_DATA_VALIDATION_BY_FIELD_TYPE[field.type](field);
      })
      // Types with no validation return undefined; an undefined entry inside Yup.object().shape()
      // makes yup's getDefaultFromShape throw when the containing object value is absent, so the
      // keys must be omitted rather than kept as undefined.
      .omitBy(_.isUndefined)
      .value()
  );
};

CustomFieldsContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  customFieldsConfigurations: PropTypes.array.isRequired,
};

const withCustomFields = (WrappedComponent) => {
  class withCustomFields extends React.Component {
    render() {
      return (
        <CustomFieldsContext.Consumer>
          {(customFieldsContext) => <WrappedComponent {...customFieldsContext} {...this.props} />}
        </CustomFieldsContext.Consumer>
      );
    }
  }

  withCustomFields.displayName = `withCustomFields(${getDisplayName(WrappedComponent)})`;
  return withCustomFields;
};

function getDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

function useCustomFields() {
  return useContext(CustomFieldsContext);
}

export default useCustomFields;
export { ADDITIONAL_DATA_FIELD_TYPES, CustomFieldsContextProvider, getAdditionalDataValidations, withCustomFields };
